// =============================================================================
// services/screenShare.ts — backend signaling for LiveKit screen sharing.
//
// IMPORTANT — two different screen-share models:
//   The React Native LiveKit SDK publishes the screen track on the user's EXISTING
//   room connection (room.localParticipant.setScreenShareEnabled(true)), fed by the
//   iOS ReplayKit broadcast extension. That is the SOURCE OF TRUTH for whether a
//   screen is actually shared — it needs NO server token.
//
//   The backend POST /voice/screen-share/start (apps/api/src/routes/voice.ts ~1938)
//   was written for the OTHER model (web/desktop publishing screen share over a
//   SECOND LiveKit connection with its own `screen_<userId>_<ts>` identity). It
//   mints a `screenShareToken` we DON'T use, and emits a Socket.IO
//   `screenShareStarted` event to the room — which IS useful (peers can show a
//   "X is sharing their screen" hint).
//
// So these helpers are BEST-EFFORT signaling only: they never gate the real
// publish, and they never throw. The actual publish/unpublish is done by the
// screens via setScreenShareEnabled().
//
// STOP SAFETY: POST /voice/screen-share/stop runs
//   liveKitService.removeParticipant(roomName, screenShareIdentity).
// If we passed our OWN connection identity (user_<id>) the backend would KICK US
// out of the call. We therefore only ever pass the server-minted `screen_*`
// identity returned by start() (which is NOT connected in the RN model, so the
// removeParticipant is a harmless no-op that still fires the stop event). If start
// didn't return one, we skip the stop call entirely.
// =============================================================================
import { api } from './api';

export type ScreenShareQuality = 'low' | 'medium' | 'high';

export interface ScreenShareStartOptions {
  quality?: ScreenShareQuality;
  frameRate?: number; // 5..30 (server clamps)
  audio?: boolean;
}

/**
 * Best-effort: tell the backend a screen share started so peers get the
 * `screenShareStarted` socket event. Returns the server-minted `screen_*`
 * identity (needed later by notifyScreenShareStop), or null on any failure.
 * Never throws and never blocks the real publish.
 */
export async function notifyScreenShareStart(
  roomName: string,
  opts: ScreenShareStartOptions = {}
): Promise<{ identity: string | null }> {
  if (!roomName) return { identity: null };
  try {
    const res = await api<{ identity?: string }>('/voice/screen-share/start', {
      method: 'POST',
      body: JSON.stringify({
        roomName,
        quality: opts.quality ?? 'medium',
        frameRate: opts.frameRate ?? 15,
        // The screen track itself carries no audio in the RN ReplayKit pipe
        // (mic/app audio stays on the main LiveKit connection), so default false.
        audio: opts.audio ?? false,
      }),
    });
    return { identity: res.success ? (res.data?.identity ?? null) : null };
  } catch {
    return { identity: null };
  }
}

/**
 * Best-effort: tell the backend a screen share stopped (fires `screenShareStopped`
 * to peers). Only sends when we have the server-minted `screen_*` identity from
 * start() — see STOP SAFETY above. Never throws.
 */
export async function notifyScreenShareStop(
  roomName: string,
  screenShareIdentity: string | null
): Promise<void> {
  if (!roomName || !screenShareIdentity) return;
  try {
    await api('/voice/screen-share/stop', {
      method: 'POST',
      body: JSON.stringify({ roomName, screenShareIdentity }),
    });
  } catch {
    /* cosmetic notification only — LiveKit already stopped the track */
  }
}
